/**
 * @typedef {Object} CvMissionDraft
 * @property {string} client
 * @property {string} role
 * @property {string} title
 * @property {string} context
 * @property {string[]} responsibilities
 * @property {string[]} environment
 */

/**
 * @typedef {Object} CvExperienceDraft
 * @property {string} company
 * @property {string} location
 * @property {string} start_date
 * @property {string} end_date
 * @property {boolean} is_current
 * @property {string} summary
 * @property {CvMissionDraft[]} missions
 */

/**
 * @typedef {Object} CvSkillCategoryDraft
 * @property {string} name
 * @property {string[]} items
 */

/**
 * @typedef {Object} CvCertificationDraft
 * @property {string} name
 * @property {string} year
 * @property {string} issuer
 */

/**
 * @typedef {Object} CvEducationDraft
 * @property {string} title
 * @property {string} institution
 * @property {string} year
 * @property {string} details
 */

/**
 * @typedef {Object} CvLanguageDraft
 * @property {string} name
 * @property {string} level
 */

/**
 * @typedef {Object} CvCandidateDraft
 * @property {string} full_name
 * @property {string} title
 * @property {string} subtitle
 * @property {number} years_of_experience
 * @property {string} location
 * @property {string} summary
 */

/**
 * @typedef {Object} CvSkillsDraft
 * @property {string[]} highlighted
 * @property {CvSkillCategoryDraft[]} categories
 */

/**
 * @typedef {Object} CvDraftPayload
 * @property {CvCandidateDraft} candidate
 * @property {CvSkillsDraft} skills
 * @property {CvCertificationDraft[]} certifications
 * @property {CvEducationDraft[]} education
 * @property {CvLanguageDraft[]} languages
 * @property {CvExperienceDraft[]} experiences
 * @property {Object|string|null|undefined} theme
 * @property {string} trigram
 */

export const CV_DRAFT_CONTRACT_VERSION = "2026-10-21-v10";

const asObject = (value) => (value && typeof value === "object" ? value : {});
const asString = (value) => (typeof value === "string" ? value : "");
const asBoolean = (value) => Boolean(value);

const toStringList = (value) => {
    const isTextList = typeof value === "string";
    let arr = value;
    if (typeof value === "string") {
        arr = value.split(/[;\n]/);
    }
    if (!Array.isArray(arr)) {
        return [];
    }
    return arr
        .map((item) => (isTextList ? asString(item).replace(/^[-•*]\s+/, "") : asString(item)).trim())
        .filter(Boolean);
};

export const createDefaultMission = () => ({
    id: globalThis.crypto?.randomUUID?.() || `project-${Date.now()}`,
    group_id: "",
    name: "",
    client: "",
    role: "",
    title: "",
    context: "",
    start_date: "",
    end_date: "",
    date_precision: "",
    is_current: false,
    source_order: null,
    responsibilities: [],
    environment: [],
    achievements: [],
    technical_environment: [],
    functional_environment: [],
    urls: [],
    sections: [],
});

export const createDefaultExperience = () => ({
    id: globalThis.crypto?.randomUUID?.() || `experience-${Date.now()}`,
    company: "",
    client: "",
    role: "",
    location: "",
    start_date: "",
    end_date: "",
    date_precision: "",
    is_current: false,
    summary: "",
    responsibilities: [],
    technical_environment: [],
    missions: [],
    projects: [],
    project_groups: [],
    achievements: [],
    functional_environment: [],
    sections: [],
});

export const createDefaultSkillCategory = () => ({
    name: "",
    items: [],
});

export const createDefaultCvDraft = () => ({
    candidate: {
        full_name: "",
        title: "",
        subtitle: "",
        years_of_experience: null,
        computed_years_of_experience: null,
        location: "",
        summary: "",
        email: "",
        phone: "",
        links: [],
    },
    skills: {
        highlighted: [],
        categories: [createDefaultSkillCategory()],
    },
    certifications: [],
    education: [],
    languages: [],
    experiences: [],
    projects: [],
    additional_sections: [],
    unmapped_content: [],
    source_metadata: {},
    theme: undefined,
    trigram: "",
});

export const computeTrigram = (fullName) => {
    if (!fullName || typeof fullName !== "string") return "";
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
        const p0 = parts[0];
        const p1 = parts[1];
        const trigram = (p0[0] || "") + (p1[0] || "") + (p1[1] || p0[1] || "");
        return trigram.toUpperCase().substring(0, 3);
    }
    return fullName.substring(0, 3).toUpperCase();
};

export const buildDefaultMission = createDefaultMission;
export const buildDefaultExperience = createDefaultExperience;

const normalizeMission = (value) => {
    const mission = asObject(value);
    const env = toStringList(mission.environment);
    const techEnv = toStringList(mission.technical_environment);
    return {
        ...mission,
        id: asString(mission.id).trim(),
        group_id: asString(mission.group_id).trim(),
        name: asString(mission.name).trim() || asString(mission.title).trim(),
        client: asString(mission.client).trim(),
        role: asString(mission.role).trim(),
        title: asString(mission.title || "").trim(),
        context: asString(mission.context || "").trim(),
        start_date: asString(mission.start_date || "").trim(),
        end_date: asString(mission.end_date || "").trim(),
        date_precision: asString(mission.date_precision || "").trim(),
        is_current: asBoolean(mission.is_current),
        source_order: Number.isInteger(mission.source_order) ? mission.source_order : null,
        responsibilities: toStringList(mission.responsibilities),
        environment: env.length > 0 ? env : techEnv,
        achievements: toStringList(mission.achievements),
        technical_environment: techEnv.length > 0 ? techEnv : env,
        functional_environment: toStringList(mission.functional_environment),
        urls: toStringList(mission.urls),
        sections: Array.isArray(mission.sections) ? mission.sections : [],
    };
};

const normalizeProject = (value) => {
    const p = asObject(value);
    return {
        ...p,
        name: asString(p.name).trim(),
        role: asString(p.role).trim(),
        description: asString(p.description).trim(),
        technologies: toStringList(p.technologies),
        start_date: asString(p.start_date).trim(),
        end_date: asString(p.end_date).trim(),
        url: asString(p.url).trim(),
    };
};

const normalizeExperience = (value) => {
    const exp = asObject(value);
    const projectIdentity = (value) => {
        const row = asObject(value);
        return row.id
        ? JSON.stringify(['id', asString(row.id).trim().toLowerCase()])
        : JSON.stringify(['fields', asString(row.group_id).trim(),
            (asString(row.name).trim() || asString(row.title).trim()).toLowerCase(),
            asString(row.client).trim().toLowerCase(), asString(row.role).trim().toLowerCase(),
            asString(row.start_date).trim(), asString(row.end_date).trim()]);
    };
    const canonical = Array.isArray(exp.projects) ? exp.projects : [];
    const legacy = Array.isArray(exp.missions) ? exp.missions : [];
    const known = new Set(canonical.map(projectIdentity));
    const projectSource = canonical.length
        ? [...canonical, ...legacy.filter(row => !known.has(projectIdentity(row)))]
        : legacy;
    const missions = projectSource
        .map(normalizeMission);

    return {
        ...exp,
        id: asString(exp.id).trim(),
        company: asString(exp.company).trim(),
        client: asString(exp.client).trim(),
        role: asString(exp.role).trim(),
        location: asString(exp.location).trim(),
        start_date: asString(exp.start_date).trim(),
        end_date: asString(exp.end_date).trim(),
        date_precision: asString(exp.date_precision).trim(),
        is_current: asBoolean(exp.is_current),
        summary: asString(exp.summary).trim(),
        responsibilities: toStringList(exp.responsibilities),
        technical_environment: toStringList(exp.technical_environment),
        missions,
        projects: missions,
        project_groups: Array.isArray(exp.project_groups) ? exp.project_groups.map(normalizeMission) : [],
        achievements: toStringList(exp.achievements),
        functional_environment: toStringList(exp.functional_environment),
        sections: Array.isArray(exp.sections) ? exp.sections : [],
    };
};

const normalizeCertification = (value) => {
    if (typeof value === "string") {
        return {
            name: value.trim(),
            year: "",
            issuer: "",
        };
    }
    const cert = asObject(value);
    return {
        ...cert,
        name: asString(cert.name).trim(),
        year: asString(cert.year).trim(),
        issuer: asString(cert.issuer).trim(),
    };
};

const normalizeEducation = (value) => {
    if (typeof value === "string") {
        return {
            title: value.trim(),
            institution: "",
            year: "",
            details: "",
        };
    }
    const edu = asObject(value);
    return {
        ...edu,
        title: asString(edu.title).trim(),
        institution: asString(edu.institution).trim(),
        year: asString(edu.year).trim(),
        details: asString(edu.details).trim(),
    };
};

const normalizeLanguage = (value) => {
    if (typeof value === "string") {
        return {
            name: value.trim(),
            level: "",
        };
    }
    const lang = asObject(value);
    return {
        ...lang,
        name: asString(lang.name).trim(),
        level: asString(lang.level).trim(),
    };
};

const normalizeSkillCategory = (value) => {
    const category = asObject(value);
    return {
        ...category,
        name: asString(category.name).trim(),
        items: toStringList(category.items),
    };
};

export const hasMeaningfulMission = (m) => {
    return Boolean(
        asString(m.name).trim() ||
        asString(m.start_date).trim() ||
        asString(m.end_date).trim() ||
        m.client ||
        m.role || 
        m.title || 
        m.context || 
        (Array.isArray(m.responsibilities) && m.responsibilities.length > 0) || 
        (Array.isArray(m.environment) && m.environment.length > 0) ||
        (Array.isArray(m.achievements) && m.achievements.length > 0) ||
        (Array.isArray(m.sections) && m.sections.length > 0) ||
        (Array.isArray(m.functional_environment) && m.functional_environment.length > 0) ||
        (Array.isArray(m.urls) && m.urls.length > 0)
    );
};

export const hasMeaningfulExperience = (exp) => {
    return Boolean(
        asString(exp.company).trim() ||
        asString(exp.role).trim() ||
        asString(exp.summary).trim() ||
        asString(exp.start_date).trim() ||
        asString(exp.end_date).trim() ||
        (Array.isArray(exp.missions) && exp.missions.some(hasMeaningfulMission)) ||
        (Array.isArray(exp.projects) && exp.projects.some(hasMeaningfulMission)) ||
        (Array.isArray(exp.project_groups) && exp.project_groups.some(hasMeaningfulMission)) ||
        (Array.isArray(exp.responsibilities) && exp.responsibilities.length > 0) ||
        (Array.isArray(exp.technical_environment) && exp.technical_environment.length > 0) ||
        (Array.isArray(exp.functional_environment) && exp.functional_environment.length > 0) ||
        (Array.isArray(exp.achievements) && exp.achievements.length > 0) ||
        (Array.isArray(exp.sections) && exp.sections.length > 0)
    );
};

export const hasMeaningfulEducation = (e) => {
    return Boolean(
        asString(e.title).trim() ||
        asString(e.institution).trim() ||
        asString(e.year).trim() ||
        asString(e.details).trim()
    );
};

export const hasMeaningfulLanguage = (l) => {
    return Boolean(
        asString(l.name).trim() ||
        asString(l.level).trim()
    );
};

/**
 * Normalize arbitrary input into the canonical CV draft payload shape.
 *
 * @param {unknown} input
 * @returns {CvDraftPayload}
 */
export const normalizeCvDraft = (input) => {
    const defaults = createDefaultCvDraft();
    const draft = asObject(input);
    const candidate = asObject(draft.candidate);
    const skills = asObject(draft.skills);

    const skillCategories = Array.isArray(skills.categories)
        ? skills.categories.map(normalizeSkillCategory)
        : defaults.skills.categories;

    return {
        ...draft,
        candidate: {
            ...candidate,
            full_name: asString(candidate.full_name).trim(),
            title: asString(candidate.title).trim(),
            subtitle: asString(candidate.subtitle).trim(),
            years_of_experience: (candidate.years_of_experience === null || candidate.years_of_experience === undefined || candidate.years_of_experience === "" || candidate.years_of_experience === 0 || candidate.years_of_experience === "0")
                ? null
                : (Number.parseInt(candidate.years_of_experience, 10) > 0 ? Number.parseInt(candidate.years_of_experience, 10) : null),
            computed_years_of_experience: (candidate.computed_years_of_experience === null || candidate.computed_years_of_experience === undefined || candidate.computed_years_of_experience === "" || candidate.computed_years_of_experience === 0 || candidate.computed_years_of_experience === "0")
                ? null
                : (Number.parseInt(candidate.computed_years_of_experience, 10) > 0 ? Number.parseInt(candidate.computed_years_of_experience, 10) : null),
            location: asString(candidate.location).trim(),
            summary: asString(candidate.summary).trim(),
            email: asString(candidate.email).trim(),
            phone: asString(candidate.phone).trim(),
            links: toStringList(candidate.links),
        },
        skills: {
            ...skills,
            highlighted: toStringList(skills.highlighted),
            categories: skillCategories.map(c => ({
                ...c,
                items: c.items.filter(Boolean)
            })).filter(c => c.name || c.items.length > 0),
        },
        certifications: (Array.isArray(draft.certifications) ? draft.certifications : [])
            .map(normalizeCertification)
            .filter((c) => c.name || c.issuer || c.year),
        education: (Array.isArray(draft.education) ? draft.education : [])
            .map(normalizeEducation)
            .filter(hasMeaningfulEducation),
        languages: (Array.isArray(draft.languages) ? draft.languages : [])
            .map(normalizeLanguage)
            .filter(hasMeaningfulLanguage),
        experiences: (Array.isArray(draft.experiences) ? draft.experiences : [])
            .map(normalizeExperience)
            .filter(hasMeaningfulExperience),
        projects: (Array.isArray(draft.projects) ? draft.projects : [])
            .map(normalizeProject)
            .filter((p) => p.name || p.description),
        additional_sections: Array.isArray(draft.additional_sections) ? draft.additional_sections : [],
        unmapped_content: Array.isArray(draft.unmapped_content)
            ? draft.unmapped_content
                .map((item) => (item && typeof item === "object" ? item : asString(item).trim()))
                .filter((item) => (item && typeof item === "object" ? Object.keys(item).length > 0 : Boolean(item)))
            : [],
        source_metadata: asObject(draft.source_metadata),
        theme: draft.theme,
        trigram: asString(draft.trigram).trim() || computeTrigram(asString(candidate.full_name)),
    };
};

/**
 * Mirror the backend CV-domain completion rules so the Builder reflects the
 * current form immediately, including before the first save.
 */
export const calculateCvCompletionScore = (input) => {
    const draft = normalizeCvDraft(input);
    const candidate = draft.candidate;
    let score = 0;

    if (candidate.full_name && candidate.title) score += 20;
    else if (candidate.full_name || candidate.title) score += 10;

    if (candidate.summary && candidate.summary.length > 20) score += 15;
    if (draft.experiences.length > 0) score += 20;
    if (draft.experiences.some((experience) => experience.missions.length > 0)) score += 15;
    if (draft.skills.highlighted.length > 0) score += 15;
    if (draft.languages.length > 0 || draft.education.length > 0) score += 15;

    return Math.min(score, 100);
};

const valueAtPath = (value, path) => path
    .split(".")
    .reduce((current, key) => (current && typeof current === "object" ? current[key] : undefined), value);

const setValueAtPath = (value, path, replacement) => {
    const keys = path.split(".");
    let current = value;
    for (let index = 0; index < keys.length - 1; index += 1) {
        const key = keys[index];
        if (!current[key] || typeof current[key] !== "object") current[key] = {};
        current = current[key];
    }
    current[keys[keys.length - 1]] = replacement;
};

/** Preserve recruiter-owned fields when a saved/imported draft is reanalysed. */
export const mergeImportedDraftPreservingUserEdits = (currentInput, importedInput) => {
    const currentDraft = normalizeCvDraft(currentInput);
    const importedDraft = normalizeCvDraft(importedInput);
    const currentProvenance = currentDraft.source_metadata?.field_provenance || {};
    const importedProvenance = importedDraft.source_metadata?.field_provenance || {};
    const retainedProvenance = {};

    Object.entries(currentProvenance).forEach(([path, origin]) => {
        if (origin !== "USER_EDITED") return;
        const retainedValue = valueAtPath(currentDraft, path);
        if (retainedValue === undefined) return;
        setValueAtPath(importedDraft, path, retainedValue);
        retainedProvenance[path] = "USER_EDITED";
    });

    importedDraft.source_metadata = {
        ...(importedDraft.source_metadata || {}),
        field_provenance: { ...importedProvenance, ...retainedProvenance },
    };
    return importedDraft;
};

/**
 * Sanitize the draft by removing empty entries and ensuring consistent formatting.
 * Alias for normalizeCvDraft with the explicit intent of cleaning up for storage/export.
 */
export const sanitizeCvDraft = (draft) => {
    return normalizeCvDraft(draft);
};
